import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { Responsive, WidthProvider } from "react-grid-layout";
import { Prompt } from "react-router";
import { noop, get } from "lodash";
import { Button, Loadable, Pane } from "components/materials";
import { EditTableViews } from "components/containers";
import SavedViews from "components/materials/FastDataTable/Controls/SavedViews";
import SaveCurrentView from "components/materials/FastDataTable/Controls/SaveCurrentView";
import ManageViews from "components/materials/FastDataTable/Controls/ManageViews";
import { toBase64, fromBase64 } from "components/materials/FastDataTable";
import { getSearchByKey, mergeSearch } from "helpers/queryStringHelpers";
import { majorScale, ThemeContext } from "helpers/utilities";
import { useFilteredProjects, UserContext } from "helpers/behaviors";
import { PERMISSION_ACTION } from "helpers/enums";
import {
  PORTFOLIO_INSIGHTS_QUERY,
  SAVE_PORTFOLIO_INSIGHTS_CONFIGURATIONS,
} from "./graphql";
import { Filters } from "./Filters";
import { filterFields, withProjectTypes } from "./helpers";

import {
  ContingencyRisk,
  CONTINGENCY_RISK_CONFIGURATION_SETTINGS,
} from "./ContingencyRisk";
import {
  DrawProcessingTime,
  DRAW_PROCESSING_TIME_CONFIGURATION_SETTINGS,
} from "./DrawProcessingTime";
import {
  OutstandingDraws,
  OUTSTANDING_DRAWS_CONFIGURATION_SETTINGS,
} from "./OutstandingDraws";
import {
  FundedDraws,
  FUNDED_DRAWS_CONFIGURATION_SETTINGS,
} from "./FundedDraws";
import {
  PortfolioComposition,
  PORTFOLIO_COMPOSITION_CONFIGURATION_SETTINGS,
} from "./PortfolioComposition";
import {
  ProjectTaskSummary,
  PROJECT_TASK_SUMMARY_CONFIGURATION_SETTINGS,
} from "./ProjectTaskSummary/ProjectTaskSummary";
import {
  ProjectCosts,
  PROJECT_COSTS_CONFIGURATION_SETTINGS,
} from "./ProjectCosts";
import {
  ProjectLocations,
  PROJECT_LOCATIONS_CONFIGURATION_SETTINGS,
} from "./ProjectLocations";
import {
  ProjectsByTeam,
  PROJECTS_BY_TEAM_CONFIGURATION_SETTINGS,
} from "./ProjectsByTeam";
import {
  ProjectsByVendor,
  PROJECTS_BY_VENDOR_CONFIGURATION_SETTINGS,
} from "./ProjectsByVendor";
import {
  ScheduleRisk,
  SCHEDULE_RISK_CONFIGURATION_SETTINGS,
} from "./ScheduleRisk";
import {
  UserActivity,
  USER_ACTIVITY_CONFIGURATION_SETTINGS,
} from "./UserActivity";

const ResponsiveGridLayout = WidthProvider(Responsive);

const PORTFOLIO_INSIGHTS_CARDS = {
  contingencyRisk: ContingencyRisk,
  drawProcessingTime: DrawProcessingTime,
  fundedDraws: FundedDraws,
  outstandingDraws: OutstandingDraws,
  portfolioComposition: PortfolioComposition,
  projectCosts: ProjectCosts,
  projectLocations: ProjectLocations,
  projectTaskSummary: ProjectTaskSummary,
  projectsByTeam: ProjectsByTeam,
  projectsByVendor: ProjectsByVendor,
  scheduleRisk: ScheduleRisk,
  userActivity: UserActivity,
};

// i - card name, x - horizontal position, y - vertical position, h - height, w - width, disabled - is card visible
const defaultCardConfiguration = [
  CONTINGENCY_RISK_CONFIGURATION_SETTINGS,
  DRAW_PROCESSING_TIME_CONFIGURATION_SETTINGS,
  FUNDED_DRAWS_CONFIGURATION_SETTINGS,
  OUTSTANDING_DRAWS_CONFIGURATION_SETTINGS,
  PORTFOLIO_COMPOSITION_CONFIGURATION_SETTINGS,
  PROJECT_COSTS_CONFIGURATION_SETTINGS,
  PROJECT_LOCATIONS_CONFIGURATION_SETTINGS,
  PROJECT_TASK_SUMMARY_CONFIGURATION_SETTINGS,
  PROJECTS_BY_TEAM_CONFIGURATION_SETTINGS,
  PROJECTS_BY_VENDOR_CONFIGURATION_SETTINGS,
  SCHEDULE_RISK_CONFIGURATION_SETTINGS,
  USER_ACTIVITY_CONFIGURATION_SETTINGS,
];

export const InsightCards = ({
  organization,
  allOrganizations,
  disabledOrganizations,
  history,
}) => {
  const { hasPermission } = useContext(UserContext);

  const { data, loading } = useQuery(PORTFOLIO_INSIGHTS_QUERY, {
    variables: { organizationId: organization.id },
  });

  const userId = get(data, "user.id");
  const projectTypes = get(data, "organization.projectTemplates", []);
  const projects = get(data, "organization.projects", []);
  const initialInsightsCards = get(
    data,
    "user.portfolioInsightsConfigurations",
    []
  );

  const preparedProjects = useMemo(
    () => withProjectTypes(projects, projectTypes),
    [projects, projectTypes]
  );

  /* eslint-disable no-unused-vars */
  const [
    filterConfig,
    filteredProjects,
    filteredTeams,
    setFilterConfig,
  ] = useFilteredProjects(preparedProjects, filterFields);
  /* eslint-enable no-unused-vars */

  const theme = useContext(ThemeContext);

  const availableCards =
    initialInsightsCards.length === 0
      ? defaultCardConfiguration
      : initialInsightsCards;

  const [isConfigurable, setIsConfigurable] = useState(false);
  const [cards, setCards] = useState([]);
  const [previousConfig, setPreviousConfig] = useState([]);
  const [saveRedirect, setSaveRedirect] = useState(false);

  useEffect(() => {
    if (!loading && data) {
      setCards(availableCards);
      setPreviousConfig(availableCards);
    }
  }, [loading, availableCards, data]);

  const handleResize = useCallback(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth > 1200) {
      setCards(previousConfig);
    }
  }, [previousConfig]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    mergeSearch(history, {
      filter: toBase64({ filterConfig }),
    });
  }, [filterConfig, history]);

  const [
    savePortfolioInsightsConfigurations,
    { loading: savePortfolioInsightsConfigurationsLoading },
  ] = useMutation(SAVE_PORTFOLIO_INSIGHTS_CONFIGURATIONS);

  const handleLayoutChange = (layout) => {
    const updatedLayout = layout.map((cardLayout) => {
      const { i, x, y, h, w } = cardLayout;
      const isDisabled = cards.filter((card) => card.i === i)[0].disabled;

      return {
        i,
        x,
        y,
        h,
        w,
        disabled: isDisabled,
      };
    });

    setCards(updatedLayout);
  };

  const handleSave = () => {
    return savePortfolioInsightsConfigurations({
      variables: {
        userId,
        organizationId: organization.id,
        portfolioInsightsConfigurations: cards,
      },
    });
  };

  if (loading || savePortfolioInsightsConfigurationsLoading)
    return <Loadable loading />;

  return (
    <EditTableViews
      canManagePublicViews={hasPermission(
        PERMISSION_ACTION.SAVE_TABLE_VIEWS,
        organization
      )}
      config={getSearchByKey(history, "filter")}
      organizationIdToScopeViews={organization.id}
      defaultViews={[]}
      tableName="PortfolioInsightsFilters"
    >
      {({
        canManagePublicViews,
        onSaveView,
        onUpdateViews,
        tableName,
        views,
      }) => {
        const scopeOrganizationsProps = {
          selectedOrganization: organization,
          allOrganizations,
          disabledOrganizations,
        };
        return (
          <Fragment>
            <SavedViews
              hydrateFromSerialized={(serializedConfig) => {
                const payload = fromBase64(serializedConfig);
                setFilterConfig(payload.filterConfig);
              }}
              tableName={tableName}
              scopeOrganizationsProps={scopeOrganizationsProps}
              views={views}
            />
            <Prompt
              when={isConfigurable}
              message={`{"confirmText": "Continue without saving", "messageText": "Continue without saving changes?"}`}
            />
            <Pane
              backgroundColor="#F4F4F6"
              display="flex"
              alignItems="center"
              paddingRight={majorScale(2)}
              paddingTop={majorScale(2)}
            >
              <Filters
                filterConfig={filterConfig}
                filterFields={filterFields}
                projects={filteredProjects}
                setFilterConfig={setFilterConfig}
              />
              <Pane display="flex" marginLeft="auto">
                <SaveCurrentView
                  canManagePublicViews={canManagePublicViews}
                  dataColumns={[]}
                  closeDropdown={noop}
                  groupConfig={{}}
                  onSaveView={onSaveView}
                  onUpdateViews={onUpdateViews}
                  saveRedirect={saveRedirect}
                  scopeOrganizationsProps={scopeOrganizationsProps}
                  setSaveRedirect={setSaveRedirect}
                  tableName={tableName}
                  views={views}
                />
                <ManageViews
                  canManagePublicViews={canManagePublicViews}
                  closeDropdown={noop}
                  onUpdateViews={onUpdateViews}
                  saveRedirect={saveRedirect}
                  setSaveRedirect={setSaveRedirect}
                  scopeOrganizationsProps={scopeOrganizationsProps}
                  views={views}
                />
                {isConfigurable ? (
                  <Pane display="flex">
                    <Button
                      onClick={() => {
                        setIsConfigurable(false);
                        setCards(previousConfig);
                      }}
                      backgroundColor="white"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        handleSave();
                        setIsConfigurable(false);
                        setPreviousConfig(cards);
                      }}
                      marginLeft={majorScale(1)}
                      appearance="primary"
                    >
                      Save
                    </Button>
                  </Pane>
                ) : (
                  <Button
                    appearance="primary"
                    onClick={() => {
                      setIsConfigurable(true);
                    }}
                  >
                    Customize
                  </Button>
                )}
              </Pane>
            </Pane>
            <ResponsiveGridLayout
              className="layout"
              layouts={{ lg: cards }}
              breakpoints={{
                lg: 1500,
                md: 1200,
                sm: 1000,
                xs: 480,
                xxs: 400,
              }}
              cols={{ lg: 2, md: 2, sm: 1, xs: 1, xxs: 1 }}
              rowHeight={400}
              compactType="vertical"
              margin={[16, 16]}
              isResizable={false}
              isDraggable={isConfigurable}
              onLayoutChange={handleLayoutChange}
            >
              {cards.map((card) => {
                const PortfolioInsightsCard = PORTFOLIO_INSIGHTS_CARDS[card.i];
                if (card.disabled && !isConfigurable) {
                  return (
                    <Pane
                      backgroundColor="#F4F4F6"
                      key={card.i}
                      height={400}
                      marginY={4}
                      marginX="auto"
                      width={600}
                      minWidth={600}
                      borderRadius={majorScale(2)}
                      isDraggable={false}
                    />
                  );
                }
                return (
                  <Pane
                    backgroundColor={
                      card.disabled ? theme.colors.gray75 : "white"
                    }
                    key={card.i}
                    height={400}
                    marginY={4}
                    marginX="auto"
                    width={600}
                    minWidth={600}
                    borderRadius={majorScale(2)}
                  >
                    <PortfolioInsightsCard
                      cards={cards}
                      history={history}
                      isConfigurable={isConfigurable}
                      isDisabled={card.disabled}
                      name={card.i}
                      organization={organization}
                      projects={filteredProjects}
                      setCards={setCards}
                      teams={filteredTeams}
                    />
                  </Pane>
                );
              })}
            </ResponsiveGridLayout>
          </Fragment>
        );
      }}
    </EditTableViews>
  );
};
